import { lazy } from 'react';

const TAG = '[lazyWithRetry]';
const RETRY_DELAYS = [1000, 3000, 5000];
const MAX_RELOADS = 2;

/**
 * Wraps React.lazy() with import retry + cache-busting page reload.
 * Customer NEVER sees an error — worst case is a couple page reloads.
 *
 * After a deploy, Vite generates new chunk hashes. Two failure modes:
 * 1. Stale index.html → references deleted chunks → 404
 * 2. Fresh index.html but chunk not yet at CDN edge → transient 404
 *
 * Strategy (3 layers, no throw):
 * Layer 1: Retry import() 3x with backoff (1s, 3s, 5s) — handles CDN propagation
 * Layer 2: Cache-busting page reload (up to 2x) — handles stale HTML
 * Layer 3: Redirect to / with clean state — nuclear reset, user lands on home page
 *
 * Per-chunk sessionStorage tracks reload count to prevent infinite loops.
 */
export function lazyWithRetry(importFn: () => Promise<{ default: React.ComponentType }>) {
  return lazy(async () => {
    // Extract import path — the only stable part after Vite minification.
    // e.g. ()=>Jt(()=>import("./ScreensPage-Cmv-Q3A4.js").then(...) → "./ScreensPage-Cmv-Q3A4.js"
    const chunk = importFn.toString().match(/import\(["']([^"']+)["']\)/)?.[1] ?? 'unknown';
    const storageKey = `lazyRetry:${chunk}`;
    const reloadCount = parseInt(sessionStorage.getItem(storageKey) || '0', 10);

    // Layer 1: retry import() with backoff (user sees loading spinner)
    for (let i = 0; i <= RETRY_DELAYS.length; i++) {
      try {
        const module = await importFn();
        if (i > 0) console.warn(`${TAG} ${chunk} succeeded on attempt ${i + 1}`);
        sessionStorage.removeItem(storageKey);
        return module;
      } catch (error) {
        console.warn(`${TAG} ${chunk} attempt ${i + 1}/${RETRY_DELAYS.length + 1} failed`, error);

        if (i < RETRY_DELAYS.length) {
          await new Promise((r) => setTimeout(r, RETRY_DELAYS[i]));
          continue;
        }

        // Layer 2: cache-busting page reload (up to MAX_RELOADS times)
        if (reloadCount < MAX_RELOADS) {
          console.warn(`${TAG} ${chunk} all retries failed, reload #${reloadCount + 1}`);
          sessionStorage.setItem(storageKey, String(reloadCount + 1));
          const url = new URL(window.location.href);
          url.searchParams.set('_cb', Date.now().toString());
          window.location.replace(url.toString());
          return new Promise(() => {});
        }

        // Layer 3: all retries + reloads exhausted — redirect to home
        console.warn(`${TAG} ${chunk} ${MAX_RELOADS} reloads exhausted, redirecting to /`);
        for (let j = sessionStorage.length - 1; j >= 0; j--) {
          const key = sessionStorage.key(j);
          if (key?.startsWith('lazyRetry:')) sessionStorage.removeItem(key);
        }
        window.location.replace('/');
        return new Promise(() => {});
      }
    }

    window.location.replace('/');
    return new Promise(() => {});
  });
}
